import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, setHours, setMinutes, isBefore, addHours } from 'date-fns';
import { toast } from 'react-toastify';

const MobileDateTimePicker = ({
    selectedDateTime,
    onChange,
    minHoursAhead = 3,
    minTime = 6, // 6 AM
    maxTime = 21, // 9 PM
    placeholder = "Pick a date & time"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(
        selectedDateTime ? format(selectedDateTime, 'yyyy-MM-dd') : ''
    );
    const [selectedTime, setSelectedTime] = useState(
        selectedDateTime ? format(selectedDateTime, 'HH:mm') : ''
    );

    // Generate next 30 days for date selection
    const generateDates = () => {
        const dates = [];
        const today = new Date();
        for (let i = 0; i < 30; i++) {
            const date = addDays(today, i);
            dates.push({
                value: format(date, 'yyyy-MM-dd'),
                label: format(date, i === 0 ? "'Today' - MMM dd" : i === 1 ? "'Tomorrow' - MMM dd" : "EEE, MMM dd"),
                date: date
            });
        }
        return dates;
    };

    // Generate time slots (30-minute intervals)
    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = minTime; hour <= maxTime; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = setMinutes(setHours(new Date(), hour), minute);
                slots.push({
                    value: format(time, 'HH:mm'),
                    label: format(time, 'h:mm aa')
                });
            }
        }
        return slots;
    };

    const dates = generateDates();
    const timeSlots = generateTimeSlots();

    const handleConfirm = () => {
        if (!selectedDate || !selectedTime) return;

        const [hours, minutes] = selectedTime.split(':').map(Number);
        const dateTime = setMinutes(setHours(new Date(selectedDate), hours), minutes);

        // Validate minimum time ahead
        const minDateTime = addHours(new Date(), minHoursAhead);
        if (isBefore(dateTime, minDateTime)) {
            toast.error(`Please select a time at least ${minHoursAhead} hours ahead.`);
            return;
        }

        onChange(dateTime);
        setIsOpen(false);
    };

    const handleCancel = () => {
        // Reset to original values
        if (selectedDateTime) {
            setSelectedDate(format(selectedDateTime, 'yyyy-MM-dd'));
            setSelectedTime(format(selectedDateTime, 'HH:mm'));
        } else {
            setSelectedDate('');
            setSelectedTime('');
        }
        setIsOpen(false);
    };

    const displayValue = selectedDateTime
        ? format(selectedDateTime, "MMM dd, yyyy 'at' h:mm aa")
        : placeholder;

    return (
        <>
            {/* Trigger Button */}
            <div
                onClick={() => setIsOpen(true)}
                className="w-full block px-4 py-3 text-sm border border-purple-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer"
            >
                <span className={selectedDateTime ? 'text-gray-900' : 'text-gray-500'}>
                    {displayValue}
                </span>
            </div>

            {/* Mobile-Optimized Modal */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 z-50"
                            onClick={handleCancel}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 max-h-[90vh] overflow-hidden"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="text-purple-600 font-medium"
                                >
                                    Cancel
                                </button>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Select Date & Time
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={!selectedDate || !selectedTime}
                                    className={`font-medium ${selectedDate && selectedTime
                                        ? 'text-purple-600'
                                        : 'text-gray-400'
                                        }`}
                                >
                                    Done
                                </button>
                            </div>

                            {/* Content */}
                            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                                {/* Date Selection */}
                                <div className="p-4 border-b border-gray-100">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">
                                        Select Date
                                    </h4>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {dates.map((date) => (
                                            <div
                                                key={date.value}
                                                onClick={() => setSelectedDate(date.value)}
                                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedDate === date.value
                                                    ? 'border-purple-500 bg-purple-50'
                                                    : 'border-gray-200 hover:border-purple-300'
                                                    }`}
                                            >
                                                <span className="text-sm font-medium text-gray-900">
                                                    {date.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Time Selection */}
                                <div className="p-4">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">
                                        Select Time
                                    </h4>
                                    <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                                        {timeSlots.map((time) => (
                                            <div
                                                key={time.value}
                                                onClick={() => setSelectedTime(time.value)}
                                                className={`p-2 text-center rounded-lg border-2 cursor-pointer transition-all ${selectedTime === time.value
                                                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                                                    : 'border-gray-200 hover:border-purple-300 text-gray-700'
                                                    }`}
                                            >
                                                <span className="text-xs font-medium">
                                                    {time.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Note */}
                                <div className="p-4 bg-gray-50 border-t border-gray-100">
                                    <p className="text-xs text-gray-600 text-center">
                                        * Bookings must be at least {minHoursAhead} hours ahead and between {format(setHours(new Date(), minTime), 'h aa')} – {format(setHours(new Date(), maxTime), 'h aa')}.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default MobileDateTimePicker; 